import { useTranslation } from 'react-i18next';
import { useEffect, useId, useState } from 'react';
import { Button } from '../../components/ui/Button';
import confirmStyles from '../../components/ui/ConfirmDialog.module.css';
import { DialogShell } from '../../components/ui/DialogShell';
import { StatusIcon } from '../../components/ui/StatusIcon';
import { useDialogDismiss } from '../../components/ui/dialogDismiss';
import type { ReverseAwardReasonCode } from '../../api/types';
import { NOTE_MAX, REVERSE_AWARD_REASONS, reverseAwardReasonValid } from './reverseAwardReason';
import styles from './ReverseAwardDialog.module.css';
import { DialogTitleRow } from '../../help/DialogTitleRow';

export interface ReverseAwardChoice {
  reasonCode: ReverseAwardReasonCode;
  /** Trimmed; null when blank. */
  note: string | null;
}

interface Props {
  isOpen: boolean;
  shipmentId: number;
  onConfirm: (choice: ReverseAwardChoice) => void;
  onCancel: () => void;
  loading?: boolean;
  /** Inline error from a refused request; the picked reason and note stay as they were. */
  errorMessage?: string | null;
}

/** Reverse an award with a required reason (#230): four flat choices, Other needs a short note. */
export function ReverseAwardDialog({
  isOpen,
  shipmentId,
  onConfirm,
  onCancel,
  loading,
  errorMessage,
}: Props) {
  const { t } = useTranslation();
  const [code, setCode] = useState<ReverseAwardReasonCode | null>(null);
  const [note, setNote] = useState('');
  const titleId = useId();
  const noteId = useId();
  const dismiss = useDialogDismiss({
    kind: 'form',
    dirty: code !== null || note.trim() !== '',
    busy: loading,
    onClose: onCancel,
  });

  useEffect(() => {
    if (isOpen) {
      setCode(null);
      setNote('');
    }
  }, [isOpen]);

  if (!isOpen) return null;

  // Only Other shows the note box, so a note typed there never rides along with another reason.
  const sentNote = code === 'OTHER' ? note : '';
  const valid = reverseAwardReasonValid(code, sentNote);
  const trimmed = sentNote.trim();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!valid || !code || loading) return;
    onConfirm({ reasonCode: code, note: trimmed || null });
  };

  return (
    <DialogShell
      dismiss={dismiss}
      labelledBy={titleId}
      scrimClassName={confirmStyles.backdrop}
      panelClassName={confirmStyles.modal}
    >
      <div className={confirmStyles.header}>
        <DialogTitleRow context="bids-and-award" title={t('shipments.reverseAwardDialog.title', { shipmentId })}>
          <h3 id={titleId} className={confirmStyles.title}>
            {t('shipments.reverseAwardDialog.title', { shipmentId })}
          </h3>
        </DialogTitleRow>
        <button
          type="button"
          className={confirmStyles.closeBtn}
          onClick={dismiss.requestClose}
          disabled={loading}
          aria-label={t('common.actions.close')}
        >
          &times;
        </button>
      </div>
      <form onSubmit={handleSubmit} className={confirmStyles.body}>
        <div className={confirmStyles.description}>
          {t('shipments.reverseAwardDialog.description')}
        </div>

        <fieldset className={styles.choices} disabled={loading}>
          <legend className={styles.legend}>{t('shipments.reverseAwardDialog.reasonLegend')}</legend>
          {REVERSE_AWARD_REASONS.map((r, i) => (
            <label
              key={r.code}
              className={`${styles.choice} ${code === r.code ? styles.choiceOn : ''}`}
            >
              <input
                type="radio"
                name="reverse-award-reason"
                value={r.code}
                checked={code === r.code}
                onChange={() => setCode(r.code)}
                className={styles.radio}
                data-autofocus={i === 0 ? true : undefined}
              />
              <span className={styles.icon}>
                <StatusIcon name={r.icon} size={20} />
              </span>
              <span className={styles.text}>
                <span className={styles.label}>{r.label}</span>
                <span className={styles.hint}>{r.hint}</span>
              </span>
            </label>
          ))}
        </fieldset>

        {code === 'OTHER' && (
          <div className={confirmStyles.reasonWrapper}>
            <label htmlFor={noteId} className={styles.noteLabel}>
              {t('shipments.reverseAwardDialog.noteLabel')}
            </label>
            <textarea
              id={noteId}
              className={confirmStyles.textarea}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              maxLength={NOTE_MAX}
              rows={3}
              disabled={loading}
              placeholder={t('shipments.reverseAwardDialog.notePlaceholder')}
            />
            <span
              className={`${styles.counter} ${trimmed.length > NOTE_MAX ? styles.counterOver : ''}`}
            >
              {trimmed.length}/{NOTE_MAX}
            </span>
          </div>
        )}

        {errorMessage && (
          <span role="alert" className={confirmStyles.errorText}>
            {errorMessage}
          </span>
        )}

        <div className={confirmStyles.actions}>
          <Button variant="secondary" onClick={dismiss.requestClose} disabled={loading}>
            {t('common.actions.cancel')}
          </Button>
          <Button
            variant="danger"
            type="submit"
            disabled={loading || !valid}
            busy={loading}
          >
            {loading ? 'Reversing…' : 'Reverse award'}
          </Button>
        </div>
      </form>
    </DialogShell>
  );
}
